import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import api, { setAccessToken, clearAccessToken } from "@/utils/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    try {
      // On page refresh, the httpOnly cookie is sent automatically.
      // The backend reads it and returns user data if valid.
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearAccessToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Store token in memory (not localStorage) for Bearer header usage
    if (data.access_token) {
      setAccessToken(data.access_token);
    }
    setUser(data);
    return data;
  }, []);

  const signup = useCallback(async (name, email, password, inviteToken) => {
    const url = inviteToken ? `/auth/register?invite_token=${inviteToken}` : "/auth/register";
    const { data } = await api.post(url, { name, email, password });
    if (data.access_token) {
      setAccessToken(data.access_token);
    }
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout endpoint may fail if token expired — safe to ignore
    }
    clearAccessToken();
    setUser(null);
    checkedRef.current = false;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, signup, logout }),
    [user, loading, login, signup, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
